/**
 * WeaveIn.ai Platform - Add/Edit Member Component (Tailwind CSS)
 * Dynamic form that renders based on organization's custom field definitions
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  ArrowLeft,
  Save,
  CheckCircle,
  Loader2,
  AlertTriangle,
  Lock,
  Eye,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import useMembers from '@/hooks/useMembers';
import useCustomFields from '@/hooks/useCustomFields';
import usePrograms from '@/hooks/usePrograms';
import { MemberCreate, CustomField, VisibilityRules, ProfileField, membersAPI } from '@/services/api/members';
import { programsAPI, ProgramCategory } from '@/services/api/programs';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useDataShield } from '@/contexts/DataShieldContext';
import { evaluateVisibilityOperator } from '../forms/fieldVisibility';
import { RevealStatusBar } from '../security/ShieldedField';
import { ReauthModal } from '../security/ReauthModal';
import {
  isNamedCategory,
  buildConfiguredGroups,
  isCategoryAllowed,
  computeRequiredCoreFields,
  type RequiredCandidate,
} from './memberFormCategories';
import { authFetch } from '@/lib/authFetch';

import { config as productConfig } from '@/src/config';
import PhoneInput from '@/components/ui/PhoneInput';
import { autoGrowTextarea, longTextRows } from '@/lib/longTextSizing';
import { orderOptions } from '@/utils/optionOrder';
import { isOffList } from '@/utils/reassignment';
interface AddEditMemberProps {
  memberId?: number;
  programId?: number; // Optional: filter fields to show only for this program
  onNavigate?: (tab: string, memberId?: number) => void;
}

// Internal form data type - uses dictionary for custom_fields (easier manipulation)
interface MemberFormData {
  first_name: string;
  last_name: string;
  email?: string;
  phone_no?: string;
  race?: string;
  ethnicity?: string;
  // TRUS-738: the form field IS the column. Preferred Language is stored, shown
  // and used by auto-translate as `preferred_language`; it used to be sent as
  // `language`, a column nothing displays or translates from.
  preferred_language?: string;
  dob?: string;
  gender?: string;
  patient_no?: string;
  medicaid_id?: string;
  medicare_id?: string;
  commercial_insurance_id?: string;
  street_address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  custom_fields?: Record<string, any>; // Dictionary format for internal use
}

export default function AddEditMemberNew({ memberId, programId, onNavigate }: AddEditMemberProps) {
  const { t } = useAppTranslation();
  const isEditMode = !!memberId;

  const { createMember, updateMember, getMember } = useMembers({ autoFetch: false });
  // Scope custom fields to member records. Without record_type the
  // /api/custom-fields endpoint skips the applies_to_record_types filter and
  // returns staff-/contact-scoped fields too; any of those marked mandatory
  // (e.g. staff_preferred_contact_method, friendly name "Preferred Contact
  // Method") then leaks into this member form and blocks creation with a
  // "<field> is required" error the user cannot satisfy (TRUS-195).
  const { fields, categories, loading: fieldsLoading } = useCustomFields({ record_type: 'member' });
  const { programs } = usePrograms();
  const { isShieldEnabled, isRevealed, requestReveal, loadEffectivePolicy, showReauthModal, setShowReauthModal, activateRevealSession, effectivePolicy } = useDataShield();

  // Track whether data is shielded in edit mode (backend returned masked values)
  const [dataIsShielded, setDataIsShielded] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);

  // Profile field configs — controls core field visibility per admin settings
  const [profileFields, setProfileFields] = useState<ProfileField[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProgramId, setSelectedProgramId] = useState<number | undefined>(programId);
  const [programCategories, setProgramCategories] = useState<ProgramCategory[]>([]);
  const [fieldOptions, setFieldOptions] = useState<{
    race: Array<{value: string; label: string}>;
    ethnicity: Array<{value: string; label: string}>;
    gender: Array<{value: string; label: string}>;
    language: Array<{value: string; label: string}>;
    country: Array<{value: string; label: string}>;
    state?: Array<{value: string; label: string}>;
  } | null>(null);

  // Core member fields - using internal format
  const [formData, setFormData] = useState<MemberFormData>({
    first_name: '',
    last_name: '',
    email: '',
    phone_no: '',
    race: '',
    ethnicity: '',
    preferred_language: '',
    dob: '',
    gender: '',
    patient_no: '',
    medicaid_id: '',
    medicare_id: '',
    commercial_insurance_id: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
    custom_fields: {},
  });

  // Store original member data for re-normalization when fieldOptions loads
  const [originalMemberData, setOriginalMemberData] = useState<{
    race?: string;
    ethnicity?: string;
    preferred_language?: string;
    gender?: string;
    country?: string;
  } | null>(null);

  // Load field options on mount
  useEffect(() => {
    const loadFieldOptions = async () => {
      try {
        const response = await authFetch(`${productConfig.weaveinApi.baseUrl}/api/members/field-options`);
        const data = await response.json();
        setFieldOptions(data);
      } catch (err) {
        console.error('Error loading field options:', err);
      }
    };

    loadFieldOptions();
  }, []);

  // Load profile field configs (core field visibility from admin settings)
  useEffect(() => {
    const loadProfileFields = async () => {
      try {
        const fields = await membersAPI.getProfileFields({ include_hidden: true });
        setProfileFields(fields);
      } catch (err) {
        console.error('Error loading profile field configs:', err);
      }
    };
    loadProfileFields();
  }, []);

  /** Check if a core field is hidden by admin config */
  const isCoreFieldHidden = (fieldKey: string): boolean => {
    const config = profileFields.find(f => f.is_core_field && f.field_key === fieldKey);
    return config?.is_hidden ?? false;
  };

  // Whether this org drives the form from the Profile Builder config. Declared
  // here rather than next to the render because the required-field derivation
  // below needs it too.
  const useConfigured = profileFields.some(
    (f) => !f.is_core_field && !f.is_hidden && f.category_key !== 'family_member' && f.category_key !== 'household'
  );

  /**
   * The core fields this form requires, as {formField: label}.
   *
   * ONE source of truth for both the submit validation and the required markers
   * on the labels. They were previously independent — validation read a
   * hardcoded list and every label hardcoded its own red asterisk — so the two
   * could disagree, and did.
   *
   * For a configured org the answer comes from the org's own field
   * configuration (`is_required`), not from any list in here. That is what the
   * Profile Builder edits and what a client's field spec is transcribed into.
   * Somos' spec marks First Name, Last Name, Phone Number and Zip Code
   * required; the old hardcoded list required `gender` (optional in their
   * config) and missed `zip_code` (required in it) — so the form disagreed with
   * the spec in both directions at once.
   *
   * Every core field the form can render is listed, so the marker set and the
   * validated set are always the same set. Adding a renderable core field here
   * is what keeps them from drifting apart again.
   */
  const requiredCoreFields = useMemo<Record<string, string>>(() => {
    // `legacyRequired` reproduces the old baseline for orgs on the legacy
    // layout, which have no per-org config to read.
    const CANDIDATES: RequiredCandidate[] = [
      { field: 'first_name', registryKey: 'first_name', label: t('members.first_name', 'First Name'), legacyRequired: true },
      { field: 'last_name', registryKey: 'last_name', label: t('members.last_name', 'Last Name'), legacyRequired: true },
      { field: 'email', registryKey: 'email', label: t('members.email', 'Email'), legacyRequired: true },
      { field: 'phone_no', registryKey: 'phone_no', label: t('members.phone_number', 'Phone Number'), legacyRequired: true },
      // Registry key is `date_of_birth`, NOT the `dob` form-field name.
      { field: 'dob', registryKey: 'date_of_birth', label: t('members.date_of_birth', 'Date of Birth'), legacyRequired: true },
      { field: 'gender', registryKey: 'gender', label: t('members.gender', 'Gender'), legacyRequired: true },
      { field: 'race', registryKey: 'race', label: t('members.race', 'Race'), legacyRequired: true },
      { field: 'ethnicity', registryKey: 'ethnicity', label: t('members.ethnicity', 'Ethnicity'), legacyRequired: true },
      // TRUS-738: form field and registry key are both `preferred_language`.
      { field: 'preferred_language', registryKey: 'preferred_language', label: t('members.primary_language', 'Primary Language'), legacyRequired: true },
      // Address block — never in the old baseline, which is why a spec that
      // requires Zip Code was silently not enforced.
      { field: 'street_address', registryKey: 'street_address', label: t('members.street_address', 'Street Address') },
      { field: 'city', registryKey: 'city', label: t('members.city', 'City') },
      { field: 'state', registryKey: 'state', label: t('members.state', 'State') },
      { field: 'zip_code', registryKey: 'zip_code', label: t('members.zip_code', 'Zip Code') },
      { field: 'country', registryKey: 'country', label: t('members.country', 'Country') },
      // Identifiers an org may require. `patient_no` is deliberately absent:
      // its label text is literally "Patient Number (Optional)", so enforcing
      // it would contradict the label — the same mismatch this map exists to
      // prevent, just in the other direction. Rename that label first if an org
      // ever needs it required.
      { field: 'medicaid_id', registryKey: 'medicaid_id', label: t('members.medicaid_id', 'Medicaid ID') },
      { field: 'medicare_id', registryKey: 'medicare_id', label: t('members.medicare_id', 'Medicare ID') },
      {
        field: 'commercial_insurance_id',
        registryKey: 'commercial_insurance_id',
        label: t('members.commercial_insurance_id', 'Commercial Insurance ID'),
      },
    ];

    return computeRequiredCoreFields(profileFields, useConfigured, CANDIDATES);
  }, [profileFields, useConfigured, t]);

  /**
   * Required marker for a core field label. Renders nothing when the field is
   * optional, so a label can no longer claim a field is required when the form
   * will happily save without it.
   */
  const RequiredMark: React.FC<{ field: string }> = ({ field }) =>
    requiredCoreFields[field] ? <span className="text-red-500">*</span> : null;

  // Load member data for edit mode
  useEffect(() => {
    if (isEditMode && memberId) {
      loadMember(memberId);
    }
  }, [isEditMode, memberId]);

  // Reload member data when reveal session activates (to get unmasked values)
  useEffect(() => {
    if (isEditMode && memberId && isRevealed && dataIsShielded) {
      loadMember(memberId);
    }
  }, [isRevealed]);

  // Re-normalize form data when fieldOptions loads (handles race condition)
  useEffect(() => {
    if (fieldOptions && originalMemberData && isEditMode) {
      setFormData(prev => ({
        ...prev,
        race: normalizeToOption(originalMemberData.race, fieldOptions.race),
        ethnicity: normalizeToOption(originalMemberData.ethnicity, fieldOptions.ethnicity),
        preferred_language: normalizeLanguage(originalMemberData.preferred_language),
        gender: normalizeToOption(originalMemberData.gender, fieldOptions.gender),
        country: normalizeToOption(originalMemberData.country, fieldOptions.country) || 'United States',
      }));
    }
  }, [fieldOptions, originalMemberData, isEditMode]);

  // Load program categories when program is selected
  useEffect(() => {
    const loadProgramCategories = async () => {
      if (selectedProgramId) {
        try {
          const categories = await programsAPI.getProgramCategories(selectedProgramId);
          setProgramCategories(categories);
        } catch (err) {
          console.error('Error loading program categories:', err);
          setProgramCategories([]);
        }
      } else {
        setProgramCategories([]);
      }
    };

    loadProgramCategories();
  }, [selectedProgramId]);

  /**
   * Normalize a value to match one of the available options.
   * Handles case differences and common abbreviations/variations.
   */
  const normalizeToOption = (
    value: string | undefined | null,
    options: Array<{value: string; label: string}> | undefined
  ): string => {
    if (!value || !options || options.length === 0) return '';

    // First, try exact match
    const exactMatch = options.find(opt => opt.value === value);
    if (exactMatch) return exactMatch.value;

    // Try case-insensitive match
    const lowerValue = value.toLowerCase().trim();
    const caseMatch = options.find(opt => opt.value.toLowerCase() === lowerValue);
    if (caseMatch) return caseMatch.value;

    // Try matching by label (case-insensitive)
    const labelMatch = options.find(opt => opt.label.toLowerCase() === lowerValue);
    if (labelMatch) return labelMatch.value;

    // Handle common abbreviations/variations
    const abbreviationMap: Record<string, string[]> = {
      'male': ['m', 'male', 'man'],
      'female': ['f', 'female', 'woman'],
      'non-binary': ['nb', 'non-binary', 'nonbinary'],
      'english': ['en', 'eng', 'english'],
      'spanish': ['es', 'esp', 'spanish', 'español'],
      'white': ['w', 'white', 'caucasian'],
      'black or african american': ['b', 'black', 'african american', 'african-american'],
      'asian': ['a', 'asian'],
      'hispanic or latino': ['h', 'hispanic', 'latino', 'latina', 'latinx'],
      'not hispanic or latino': ['nh', 'not hispanic', 'non-hispanic'],
    };

    for (const option of options) {
      const optionKey = option.value.toLowerCase();
      const aliases = abbreviationMap[optionKey];
      if (aliases && aliases.includes(lowerValue)) {
        return option.value;
      }
    }

    // If no match found, return empty string (will show placeholder)
    // Log warning for debugging
    console.warn(`Could not normalize value "${value}" to any available option`);
    return '';
  };

  /**
   * Build the option list for a searchable picker (State/Country), preserving any
   * legacy free-text value that predates the canonical list so an existing member's
   * value still renders (and stays selected) instead of showing a blank field.
   */
  const optionsWithCurrent = (
    options: Array<{value: string; label: string}> | undefined,
    current: string | undefined,
  ): Array<{value: string; label: string}> => {
    const base = options ?? [];
    if (current && !base.some(o => o.value === current)) {
      return [{ value: current, label: current }, ...base];
    }
    return base;
  };

  /**
   * Preferred Language: match the stored value to a dropdown option, but KEEP a
   * value that matches none (e.g. "Tagalog", "Cantonese" from an import).
   * normalizeToOption returns '' for an unmatched value, which used to be
   * harmless on the unused `language` column; on `preferred_language` it would
   * erase the member's language on the next Save (TRUS-738). The kept value is
   * offered in the dropdown via optionsWithCurrent.
   */
  const normalizeLanguage = (value: string | undefined | null): string =>
    normalizeToOption(value, fieldOptions?.language) || (value ?? '').trim();

  const loadMember = async (memberId: number) => {
    setLoading(true);
    const member = await getMember(memberId);
    if (member) {
      // Detect if backend returned shielded data
      const shielded = !!(member as any)._shielded;
      setDataIsShielded(shielded);

      // Detect view-only access (user is not the assigned staff). Server will
      // reject PUT anyway, but we disable the form to avoid wasted edits.
      setIsViewOnly(member.access_level === 'view_only');

      // Load effective policy for this member
      try {
        const cboId = parseInt(localStorage.getItem('active_cbo_id') || '0', 10);
        if (cboId) {
          await loadEffectivePolicy(cboId, memberId);
        }
      } catch (err) {
        console.error('Failed to load effective policy for edit form:', err);
      }

      // Store original values for re-normalization when fieldOptions loads
      setOriginalMemberData({
        race: member.race,
        ethnicity: member.ethnicity,
        preferred_language: member.preferred_language,
        gender: member.gender,
        country: member.country,
      });

      // If fieldOptions is loaded, normalize immediately; otherwise use raw values
      // The useEffect will re-normalize when fieldOptions loads
      const normalizedFormData: MemberFormData = {
        first_name: member.first_name,
        last_name: member.last_name,
        email: member.email || '',
        phone_no: member.phone_no || '',
        race: fieldOptions ? normalizeToOption(member.race, fieldOptions.race) : (member.race || ''),
        ethnicity: fieldOptions ? normalizeToOption(member.ethnicity, fieldOptions.ethnicity) : (member.ethnicity || ''),
        preferred_language: fieldOptions ? normalizeLanguage(member.preferred_language) : (member.preferred_language || ''),
        dob: member.dob || '',
        gender: fieldOptions ? normalizeToOption(member.gender, fieldOptions.gender) : (member.gender || ''),
        patient_no: member.patient_no || '',
        medicaid_id: member.medicaid_id || '',
        medicare_id: member.medicare_id || '',
        commercial_insurance_id: member.commercial_insurance_id || '',
        street_address: member.street_address || '',
        city: member.city || '',
        state: member.state || '',
        zip_code: member.zip_code || '',
        country: fieldOptions ? normalizeToOption(member.country, fieldOptions.country) : (member.country || 'United States'),
        custom_fields: member.custom_fields || {},
      };

      setFormData(normalizedFormData);
    }
    setLoading(false);
  };

  const handleCoreFieldChange = (field: keyof MemberFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCustomFieldChange = (fieldName: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      custom_fields: {
        ...prev.custom_fields,
        [fieldName]: value,
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    for (const [field, label] of Object.entries(requiredCoreFields)) {
      if (!formData[field as keyof MemberFormData]) {
        setError(t('members.fieldIsRequired', '{{field}} is required', { field: label }));
        setLoading(false);
        return;
      }
    }

    // Validate mandatory custom fields - ONLY for fields visible on this form
    const visibleMandatoryFields = fields?.filter((f) => {
      if (!f.mandatory || f.hidden) return false;

      // Skip fields hidden by visibility_rules (conditional visibility)
      if (!isFieldVisible(f)) return false;

      // If no program selected, all fields are visible
      if (!selectedProgramId) return true;

      // If program selected with no categories, all fields are visible (no restriction)
      if (programCategories.length === 0) return true;

      // If program has categories, only validate fields in those categories.
      // Same tolerant comparison the render path uses, so a field can never be
      // shown on the form but skipped by validation (or vice versa).
      const allowedCategories = programCategories.map(pc => pc.category);
      return !!f.category && isCategoryAllowed(allowedCategories, { name: f.category });
    }) || [];

    for (const field of visibleMandatoryFields) {
      if (!formData.custom_fields?.[field.name]) {
        setError(t('members.fieldIsRequired', '{{field}} is required', { field: field.friendly_name }));
        setLoading(false);
        return;
      }
    }

    // Transform custom_fields from dictionary to array format for API
    const customFieldsArray = Object.entries(formData.custom_fields || {}).map(([field_name, value]) => ({
      field_name,
      value,
    }));

    const memberPayload: MemberCreate = {
      ...formData,
      custom_fields: customFieldsArray,
    };

    try {
      if (isEditMode && memberId) {
        const updated = await updateMember(memberId, memberPayload);
        if (updated) {
          onNavigate?.('members-view', memberId);
        } else {
          // Belt and braces. useMembers now THROWS on failure, so this branch
          // should be unreachable — but it is exactly the branch that used to
          // swallow every save error: the hook resolved with null, this `if`
          // was false, and the user got no navigation and no message, just a
          // button that stopped spinning. Never fail silently here again.
          setError(t('members.failedToSave', 'Failed to save member'));
        }
      } else {
        const created = await createMember(memberPayload);
        if (created) {
          onNavigate?.('members-view', created.id);
        } else {
          setError(t('members.failedToSave', 'Failed to save member'));
        }
      }
    } catch (err: any) {
      setError(err.message || t('members.failedToSave', 'Failed to save member'));
    } finally {
      setLoading(false);
    }
  };

  // Get categories to display based on program selection
  const getDisplayCategories = () => {
    if (!selectedProgramId) {
      // No program selected - show all categories
      return categories;
    }

    if (programCategories.length === 0) {
      // Program selected but has NO custom field categories configured
      // Fall through to show ALL categories (no restriction)
      return categories;
    }

    // Program selected WITH categories - show only those categories
    const allowedCategories = programCategories.map(pc => pc.category);
    return categories?.filter(cat => isCategoryAllowed(allowedCategories, { name: cat }));
  };

  const displayCategories = getDisplayCategories();

  /**
   * Custom field display order is configured via drag-and-drop on the field
   * config admin page, which writes member_field_configurations.sort_order.
   * That value is exposed here through the profile-fields endpoint as
   * `field_sort`, keyed by the underlying custom field name. Without this
   * lookup, the form falls back to com_fields' natural order (friendly_name
   * ASC), which silently ignores admin drag-reorder (TRUS-69).
   */
  const customFieldSortByName = useMemo(() => {
    const m = new Map<string, number>();
    for (const pf of profileFields) {
      if (!pf.is_core_field && typeof pf.field_sort === 'number') {
        m.set(pf.field_key, pf.field_sort);
      }
    }
    return m;
  }, [profileFields]);

  const compareBySortOrder = (a: CustomField, b: CustomField): number => {
    const sa = customFieldSortByName.get(a.name);
    const sb = customFieldSortByName.get(b.name);
    // Fields without a configured sort_order sink below configured ones, then
    // tiebreak alphabetically to preserve the prior visual order for orgs
    // that haven't reordered anything.
    if (sa !== undefined && sb !== undefined) return sa - sb;
    if (sa !== undefined) return -1;
    if (sb !== undefined) return 1;
    return (a.friendly_name || '').localeCompare(b.friendly_name || '');
  };

  // When data is shielded and not revealed, sensitive fields should be read-only
  const sensitiveFieldsLocked = isEditMode && dataIsShielded && !isRevealed;

  // Lock the whole form when the user does not have edit rights on this member.
  // This applies both to shielded fields and to view-only access (where the
  // user can read this member through program access but is not the assigned
  // staff). The Save button is separately disabled with a clearer message.
  const formLocked = sensitiveFieldsLocked || isViewOnly;

  /**
   * Resolve the value of a dependency referenced in a visibility/required rule.
   * Tolerant of admin entering friendly_name, mixed case, or spaces instead of
   * the underlying snake_case field name — a common source of "the conditional
   * field didn't adjust" bugs (TRUS-49).
   */
  const resolveDependencyValue = (fieldRef: string): any => {
    if ((formData as any)[fieldRef] !== undefined) return (formData as any)[fieldRef];
    if (formData.custom_fields?.[fieldRef] !== undefined) return formData.custom_fields[fieldRef];

    const normalized = fieldRef.toLowerCase().replace(/[\s-]+/g, '_');
    const match = fields?.find((f) =>
      f.name === fieldRef ||
      f.name.toLowerCase() === fieldRef.toLowerCase() ||
      f.name.toLowerCase() === normalized ||
      f.friendly_name === fieldRef ||
      f.friendly_name?.toLowerCase() === fieldRef.toLowerCase()
    );
    return match ? formData.custom_fields?.[match.name] : undefined;
  };

  /**
   * Evaluate whether a field should be visible based on its visibility_rules.
   * Returns true if the field should be shown.
   */
  const isFieldVisible = (field: CustomField): boolean => {
    const rules = field.visibility_rules;
    if (!rules || !rules.conditions || rules.conditions.length === 0) return true;

    const results = rules.conditions.map((cond) => {
      const depValue = resolveDependencyValue(cond.field);
      // Operator semantics are shared with the form renderers so a rule behaves
      // identically everywhere. This also collapses multi-select values — which
      // this form persists as a JSON-encoded array string — to option
      // membership, so "equals Other" fires when Other is among the selections
      // instead of comparing against the raw '["Other"]' text (TRUS-289).
      return evaluateVisibilityOperator(cond.operator, depValue, cond.value);
    });

    return rules.logic === 'OR'
      ? results.some(Boolean)
      : results.every(Boolean);
  };

  const renderCustomField = (field: CustomField) => {
    // Skip fields whose visibility conditions are not met
    if (!isFieldVisible(field)) return null;

    const value = formData.custom_fields?.[field.name] || '';

    switch (field.type) {
      case 'bool':
        return (
          <div key={field.id} className="col-span-12 sm:col-span-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`field-${field.id}`}
                checked={!!value}
                onCheckedChange={(checked) => handleCustomFieldChange(field.name, checked)}
              />
              <Label
                htmlFor={`field-${field.id}`}
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {field.friendly_name}
                {field.mandatory && <span className="text-red-500"> *</span>}
              </Label>
            </div>
          </div>
        );

      case 'date':
      case 'fixed_date':
        return (
          <div key={field.id} className="col-span-12 sm:col-span-6">
            <Label htmlFor={`field-${field.id}`} className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {field.friendly_name}
              {field.mandatory && <span className="text-red-500"> *</span>}
            </Label>
            <Input
              id={`field-${field.id}`}
              type="date"
              value={value}
              onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
              required={field.mandatory}
              className="mt-1"
            />
          </div>
        );

      case 'datetime':
        return (
          <div key={field.id} className="col-span-12 sm:col-span-6">
            <Label htmlFor={`field-${field.id}`} className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {field.friendly_name}
              {field.mandatory && <span className="text-red-500"> *</span>}
            </Label>
            <Input
              id={`field-${field.id}`}
              type="datetime-local"
              value={value}
              onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
              required={field.mandatory}
              className="mt-1"
            />
          </div>
        );

      case 'time':
        return (
          <div key={field.id} className="col-span-12 sm:col-span-6">
            <Label htmlFor={`field-${field.id}`} className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {field.friendly_name}
              {field.mandatory && <span className="text-red-500"> *</span>}
            </Label>
            <Input
              id={`field-${field.id}`}
              type="time"
              value={value}
              onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
              required={field.mandatory}
              className="mt-1"
            />
          </div>
        );

      case 'int':
        return (
          <div key={field.id} className="col-span-12 sm:col-span-6">
            <Label htmlFor={`field-${field.id}`} className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {field.friendly_name}
              {field.mandatory && <span className="text-red-500"> *</span>}
            </Label>
            <Input
              id={`field-${field.id}`}
              type="text"
              inputMode="numeric"
              value={value}
              onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '' || (!isNaN(Number(raw)) && Number(raw) >= 0)) {
                  handleCustomFieldChange(field.name, raw);
                }
              }}
              required={field.mandatory}
              className="mt-1"
            />
          </div>
        );

      case 'select':
        return (
          <div key={field.id} className="col-span-12 sm:col-span-6">
            <Label htmlFor={`field-${field.id}`} className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {field.friendly_name}
              {field.mandatory && <span className="text-red-500"> *</span>}
            </Label>
            <Select
              value={value || ''}
              onValueChange={(val) => handleCustomFieldChange(field.name, val)}
            >
              <SelectTrigger className="mt-1 w-full">
                <SelectValue placeholder={t('members.selectFieldPrompt', 'Select {{field}}', { field: field.friendly_name })} />
              </SelectTrigger>
              <SelectContent>
                {!field.mandatory && (
                  <SelectItem value="">{t('members.noneOption', '— None —')}</SelectItem>
                )}
                {/* A saved value that is no longer on the list (e.g. a DCM who left) is
                    shown as the current choice instead of a blank select, and cannot be
                    picked again (TRUS-716). */}
                {isOffList(field, value) && (
                  <SelectItem value={String(value)} disabled>
                    {t('members.offListOption', '{{value}} (no longer on the list)', { value: String(value) })}
                  </SelectItem>
                )}
                {orderOptions(field.field_options).map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'multi_select': {
        const selected: string[] = (() => {
          try {
            return Array.isArray(value) ? value : value ? JSON.parse(value) : [];
          } catch {
            return [];
          }
        })();
        return (
          <div key={field.id} className="col-span-12 sm:col-span-6">
            <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {field.friendly_name}
              {field.mandatory && <span className="text-red-500"> *</span>}
            </Label>
            <div className="mt-1 space-y-1 border rounded-md p-2 max-h-72 overflow-y-auto">
              {orderOptions(field.field_options).map((opt) => (
                <div key={opt.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`field-${field.id}-${opt.value}`}
                    checked={selected.includes(opt.value)}
                    onCheckedChange={(checked) => {
                      const next = checked
                        ? [...selected, opt.value]
                        : selected.filter((v) => v !== opt.value);
                      handleCustomFieldChange(field.name, JSON.stringify(next));
                    }}
                  />
                  <Label htmlFor={`field-${field.id}-${opt.value}`} className="text-sm font-normal">
                    {opt.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        );
      }

      case 'checkbox': {
        const checked: string[] = (() => {
          try {
            return Array.isArray(value) ? value : value ? JSON.parse(value) : [];
          } catch {
            return [];
          }
        })();
        return (
          <div key={field.id} className="col-span-12">
            <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {field.friendly_name}
              {field.mandatory && <span className="text-red-500"> *</span>}
            </Label>
            <div className="mt-1 flex flex-wrap gap-4">
              {orderOptions(field.field_options).map((opt) => (
                <div key={opt.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`field-${field.id}-${opt.value}`}
                    checked={checked.includes(opt.value)}
                    onCheckedChange={(isChecked) => {
                      const next = isChecked
                        ? [...checked, opt.value]
                        : checked.filter((v) => v !== opt.value);
                      handleCustomFieldChange(field.name, JSON.stringify(next));
                    }}
                  />
                  <Label htmlFor={`field-${field.id}-${opt.value}`} className="text-sm font-normal">
                    {opt.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        );
      }

      case 'long_text':
      case 'str':
      default: {
        const maxLen = field.str_len || 128;
        const isLongText = field.type === 'long_text' || maxLen > 255;
        return (
          <div key={field.id} className={isLongText ? 'col-span-12' : 'col-span-12 sm:col-span-6'}>
            <Label htmlFor={`field-${field.id}`} className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {field.friendly_name}
              {field.mandatory && <span className="text-red-500"> *</span>}
            </Label>
            {isLongText ? (
              // Sized from the field's own cap and grown to fit what is actually
              // in it: a narrative case-note field opens tall instead of showing
              // a 33,000-character history through a four-row slit (TRUS-496).
              <Textarea
                id={`field-${field.id}`}
                value={value}
                onChange={(e) => {
                  handleCustomFieldChange(field.name, e.target.value);
                  autoGrowTextarea(e.target as HTMLTextAreaElement);
                }}
                ref={autoGrowTextarea}
                maxLength={maxLen}
                required={field.mandatory}
                rows={longTextRows(field.str_len)}
                className="mt-1"
              />
            ) : (
              <Input
                id={`field-${field.id}`}
                type="text"
                value={value}
                onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
                maxLength={maxLen}
                required={field.mandatory}
                className="mt-1"
              />
            )}
          </div>
        );
      }
    }
  };

  /**
   * Render a single hardcoded core field by its config `field_key`. The JSX for
   * each core control is moved here verbatim from the legacy fixed layout so the
   * data-driven configured layout (TRUS-290) reuses the exact same controls,
   * handlers, and i18n strings. Returns null for a config field_key that has no
   * matching hardcoded control (e.g. ssn_last_four) — we never invent a control.
   *
   * Note the field_key → control mapping quirks:
   *  - `preferred_language` drives the `formData.preferred_language` control
   *    (options still come from fieldOptions.language, the /field-options key);
   *  - `phone_no` uses the PhoneInput control;
   *  - both `dob` and `date_of_birth` map to the Date of Birth control.
   * Per-field `isCoreFieldHidden(...)` guards are preserved where they existed.
   */
  const renderCoreField = (fieldKey: string): JSX.Element | null => {
    switch (fieldKey) {
      case 'first_name':
        return (
          <div className="col-span-12 sm:col-span-6">
            <Label htmlFor="first-name" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('members.first_name', 'First Name')} <RequiredMark field="first_name" />
            </Label>
            <Input
              id="first-name"
              type="text"
              required
              value={formData.first_name}
              onChange={(e) => handleCoreFieldChange('first_name', e.target.value)}
              disabled={formLocked}
              className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
            />
          </div>
        );

      case 'last_name':
        return (
          <div className="col-span-12 sm:col-span-6">
            <Label htmlFor="last-name" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('members.last_name', 'Last Name')} <RequiredMark field="last_name" />
            </Label>
            <Input
              id="last-name"
              type="text"
              required
              value={formData.last_name}
              onChange={(e) => handleCoreFieldChange('last_name', e.target.value)}
              disabled={formLocked}
              className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
            />
          </div>
        );

      case 'email':
        return (
          <div className="col-span-12 sm:col-span-6">
            <Label htmlFor="email" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('members.email', 'Email')} <RequiredMark field="email" />
            </Label>
            <Input
              id="email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => handleCoreFieldChange('email', e.target.value)}
              placeholder={t('members.emailPlaceholder', 'member@example.com')}
              disabled={formLocked}
              className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
            />
          </div>
        );

      case 'phone_no':
        return (
          <div className="col-span-12 sm:col-span-6">
            <Label htmlFor="phone" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('members.phone_number', 'Phone Number')} <RequiredMark field="phone_no" />
            </Label>
            <PhoneInput
              id="phone"
              required
              value={formData.phone_no}
              onChange={(v) => handleCoreFieldChange('phone_no', v)}
              placeholder="(415) 336-2326"
              disabled={formLocked}
              className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('members.phoneFormatHint', 'Format: (xxx) xxx-xxxx - will be auto-formatted')}
            </p>
          </div>
        );

      case 'dob':
      case 'date_of_birth':
        return (
          <div className="col-span-12 sm:col-span-6">
            <Label htmlFor="dob" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('members.date_of_birth', 'Date of Birth')} <RequiredMark field="dob" />
            </Label>
            <Input
              id="dob"
              type="date"
              required
              value={formData.dob}
              onChange={(e) => handleCoreFieldChange('dob', e.target.value)}
              disabled={formLocked}
              className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
            />
          </div>
        );

      case 'gender':
        return (
          <div className="col-span-12 sm:col-span-6">
            <Label htmlFor="gender" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              {t('members.gender', 'Gender')} <RequiredMark field="gender" />
            </Label>
            <Select
              value={formData.gender}
              onValueChange={(value) => handleCoreFieldChange('gender', value)}
            >
              <SelectTrigger id="gender">
                <SelectValue placeholder={t('members.select_gender', 'Select Gender...')} />
              </SelectTrigger>
              <SelectContent>
                {fieldOptions?.gender.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'race':
        if (isCoreFieldHidden('race')) return null;
        return (
          <div className="col-span-12 sm:col-span-6">
            <Label htmlFor="race" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              {t('members.race', 'Race')} <RequiredMark field="race" />
            </Label>
            <Select
              value={formData.race}
              onValueChange={(value) => handleCoreFieldChange('race', value)}
            >
              <SelectTrigger id="race">
                <SelectValue placeholder={t('members.select_race', 'Select Race...')} />
              </SelectTrigger>
              <SelectContent>
                {fieldOptions?.race.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'ethnicity':
        if (isCoreFieldHidden('ethnicity')) return null;
        return (
          <div className="col-span-12 sm:col-span-6">
            <Label htmlFor="ethnicity" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              {t('members.ethnicity', 'Ethnicity')} <RequiredMark field="ethnicity" />
            </Label>
            <Select
              value={formData.ethnicity}
              onValueChange={(value) => handleCoreFieldChange('ethnicity', value)}
            >
              <SelectTrigger id="ethnicity">
                <SelectValue placeholder={t('members.select_ethnicity', 'Select Ethnicity...')} />
              </SelectTrigger>
              <SelectContent>
                {fieldOptions?.ethnicity.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'preferred_language':
        if (isCoreFieldHidden('preferred_language')) return null;
        return (
          <div className="col-span-12 sm:col-span-6">
            <Label htmlFor="language" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              {t('members.primary_language', 'Primary Language')} <RequiredMark field="preferred_language" />
            </Label>
            <Select
              value={formData.preferred_language}
              onValueChange={(value) => handleCoreFieldChange('preferred_language', value)}
            >
              <SelectTrigger id="language">
                <SelectValue placeholder={t('members.select_language', 'Select Language...')} />
              </SelectTrigger>
              <SelectContent>
                {optionsWithCurrent(fieldOptions?.language, formData.preferred_language).map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'patient_no':
        if (isCoreFieldHidden('patient_no')) return null;
        return (
          <div className="col-span-12 sm:col-span-6">
            <Label htmlFor="patient-no" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('members.patientNumberOptional', 'Patient Number (Optional)')}
            </Label>
            <Input
              id="patient-no"
              type="text"
              value={formData.patient_no}
              onChange={(e) => handleCoreFieldChange('patient_no', e.target.value)}
              placeholder={t('members.externalPatientId', 'External patient ID (optional)')}
              className="mt-1"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('members.patientNumberHint', 'External identifier from another system (e.g., legacy EHR). Internal Member ID is auto-generated.')}
            </p>
          </div>
        );

      case 'medicaid_id':
        if (isCoreFieldHidden('medicaid_id')) return null;
        return (
          <div className="col-span-12 sm:col-span-6 md:col-span-4">
            <Label htmlFor="medicaid-id" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('members.medicaid_id', 'Medicaid ID')} <RequiredMark field="medicaid_id" />
            </Label>
            <Input
              id="medicaid-id"
              type="text"
              value={formData.medicaid_id}
              onChange={(e) => handleCoreFieldChange('medicaid_id', e.target.value)}
              placeholder={t('members.medicaid_id_1', 'Medicaid ID')}
              disabled={formLocked}
              className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
            />
          </div>
        );

      case 'medicare_id':
        if (isCoreFieldHidden('medicare_id')) return null;
        return (
          <div className="col-span-12 sm:col-span-6 md:col-span-4">
            <Label htmlFor="medicare-id" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('members.medicare_id', 'Medicare ID')} <RequiredMark field="medicare_id" />
            </Label>
            <Input
              id="medicare-id"
              type="text"
              value={formData.medicare_id}
              onChange={(e) => handleCoreFieldChange('medicare_id', e.target.value)}
              placeholder={t('members.medicare_id_1', 'Medicare ID')}
              disabled={formLocked}
              className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
            />
          </div>
        );

      case 'commercial_insurance_id':
        if (isCoreFieldHidden('commercial_insurance_id')) return null;
        return (
          <div className="col-span-12 sm:col-span-6 md:col-span-4">
            <Label htmlFor="commercial-insurance-id" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('members.commercial_insurance_id', 'Commercial Insurance ID')} <RequiredMark field="commercial_insurance_id" />
            </Label>
            <Input
              id="commercial-insurance-id"
              type="text"
              value={formData.commercial_insurance_id}
              onChange={(e) => handleCoreFieldChange('commercial_insurance_id', e.target.value)}
              placeholder={t('members.commercial_insurance_id_1', 'Commercial Insurance ID')}
              disabled={formLocked}
              className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
            />
          </div>
        );

      case 'street_address':
        return (
          <div className="col-span-12">
            <Label htmlFor="street-address" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('members.street_address', 'Street Address')} <RequiredMark field="street_address" />
            </Label>
            <Input
              id="street-address"
              type="text"
              value={formData.street_address}
              onChange={(e) => handleCoreFieldChange('street_address', e.target.value)}
              placeholder={t('members.street_address_1', 'Street Address')}
              disabled={formLocked}
              className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
            />
          </div>
        );

      case 'city':
        return (
          <div className="col-span-12 sm:col-span-6 md:col-span-4">
            <Label htmlFor="city" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('members.city', 'City')} <RequiredMark field="city" />
            </Label>
            <Input
              id="city"
              type="text"
              value={formData.city}
              onChange={(e) => handleCoreFieldChange('city', e.target.value)}
              placeholder={t('members.city_1', 'City')}
              disabled={formLocked}
              className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
            />
          </div>
        );

      case 'state':
        return (
          <div className="col-span-12 sm:col-span-6 md:col-span-4">
            <Label htmlFor="state" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              {t('members.state', 'State')} <RequiredMark field="state" />
            </Label>
            <SearchableSelect
              id="state"
              options={optionsWithCurrent(fieldOptions?.state, formData.state)}
              value={formData.state}
              onValueChange={(value) => handleCoreFieldChange('state', value)}
              placeholder={t('members.select_state', 'Select State...')}
              searchPlaceholder={t('members.search_state', 'Search states...')}
              disabled={formLocked}
              className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
            />
          </div>
        );

      case 'zip_code':
        return (
          <div className="col-span-12 sm:col-span-6 md:col-span-2">
            <Label htmlFor="zip-code" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('members.zip_code', 'Zip Code')} <RequiredMark field="zip_code" />
            </Label>
            <Input
              id="zip-code"
              type="text"
              value={formData.zip_code}
              onChange={(e) => handleCoreFieldChange('zip_code', e.target.value)}
              placeholder={t('members.zip_code_1', 'Zip Code')}
              disabled={formLocked}
              className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
            />
          </div>
        );

      case 'country':
        return (
          <div className="col-span-12 sm:col-span-6 md:col-span-2">
            <Label htmlFor="country" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              {t('members.country', 'Country')} <RequiredMark field="country" />
            </Label>
            <SearchableSelect
              id="country"
              options={optionsWithCurrent(fieldOptions?.country, formData.country)}
              value={formData.country}
              onValueChange={(value) => handleCoreFieldChange('country', value)}
              placeholder={t('members.select_country', 'Select Country...')}
              searchPlaceholder={t('members.search_country', 'Search countries...')}
              disabled={formLocked}
              className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
            />
          </div>
        );

      default:
        // Config field_key with no matching hardcoded control (e.g. ssn_last_four).
        return null;
    }
  };

  /**
   * Data-driven Demographics layout for orgs that use the Profile Builder.
   * Mirrors MemberProfileNew.renderConfiguredFields() grouping/order exactly so
   * the Add form structure matches the Profile (TRUS-290): sections built from
   * `profileFields` via buildConfiguredGroups, core fields rendered by
   * renderCoreField and custom fields by renderCustomField.
   */
  const renderConfiguredLayout = () => {
    const groups = buildConfiguredGroups(profileFields);
    const customByName = new Map<string, CustomField>((fields || []).map((f) => [f.name, f]));

    // Program filtering: when a specific program with configured categories is
    // selected, only show a group whose category name is allowed OR that
    // contains a core field (core fields must always stay reachable, mirroring
    // the always-shown core sections in the legacy layout). No program selected
    // (or a program with no categories) → show all groups.
    const allowedCategories =
      selectedProgramId && programCategories.length > 0
        ? programCategories.map((pc) => pc.category)
        : null;

    return groups.map((group) => {
      const hasCore = group.items.some((pf) => pf.is_core_field);
      // Match on the category's KEY as well as its display name. Matching the
      // display name alone made a section's visibility hinge on a label admins
      // can rename: renaming "Status" to "Engagement" dropped the section and
      // every field in it from this form, silently.
      if (
        !hasCore &&
        !isCategoryAllowed(allowedCategories, { key: group.key, name: group.name })
      ) {
        return null;
      }

      const items = group.items
        .map((pf) => {
          const el = pf.is_core_field
            ? renderCoreField(pf.field_key)
            : (() => {
                const cf = customByName.get(pf.field_key);
                return cf ? renderCustomField(cf) : null;
              })();
          return el ? <React.Fragment key={pf.field_key}>{el}</React.Fragment> : null;
        })
        .filter(Boolean);

      if (items.length === 0) return null;

      return (
        <Card key={group.key} className="mb-6">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {group.name}
            </h2>
            <Separator className="mb-6" />
            <div className="grid grid-cols-12 gap-6">
              {items}
            </div>
          </CardContent>
        </Card>
      );
    });
  };

  // Demographics render data-driven from the admin's Profile Builder config when
  // the org actually uses it, else the legacy fixed layout. `useConfigured` is
  // declared above because the required-field derivation needs it too.

  if (fieldsLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px] flex-col gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <p className="text-base text-gray-600 dark:text-gray-400">
          {t('members.loading_form', 'Loading form...')}
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Toolbar */}
      <div className="mb-6 flex flex-wrap items-center justify-end gap-2">
        {/* When editing an existing member, offer a way back to that member's
            profile so staff can keep working within the file instead of only
            dropping to the list and re-searching for them (TRUS-581). The
            members-view target + memberId is the same path the save handler
            uses on success. */}
        {isEditMode && memberId && (
          <Button
            variant="outline"
            icon={<ArrowLeft className="w-4 h-4" />}
            iconPosition="left"
            onClick={() => onNavigate?.('members-view', memberId)}
          >
            {t('members.back_to_profile', 'Back to Profile')}
          </Button>
        )}
        <Button
          variant="outline"
          icon={<ArrowLeft className="w-4 h-4" />}
          iconPosition="left"
          onClick={() => onNavigate?.('members')}
        >
          {t('members.back_to_list', 'Back to List')}
        </Button>
      </div>

      {/* Data Shield Warning — shown in edit mode when data is masked */}
      {isEditMode && dataIsShielded && !isRevealed && (
        <Alert className="mb-6 border-amber-400 bg-amber-50 dark:bg-amber-900/20">
          <Lock className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-900 dark:text-amber-100 font-semibold">
            {t('members.data_shielded', 'Sensitive fields are shielded')}
          </AlertTitle>
          <AlertDescription className="text-amber-800 dark:text-amber-200 text-sm">
            {t('members.reveal_to_edit', 'Some fields display masked values due to your organization\'s privacy policy. You must reveal data before editing these fields.')}
            <button
              type="button"
              onClick={requestReveal}
              className="ml-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100 text-xs font-medium hover:bg-amber-300 dark:hover:bg-amber-700 transition-colors"
            >
              <Eye className="w-3.5 h-3.5" />
              {t('members.reveal_to_edit_btn', 'Reveal to Edit')}
            </button>
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        {isViewOnly && (
          <div className="mb-6 p-4 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20">
            <p className="text-sm text-amber-900 dark:text-amber-200">
              <strong>{t('members.viewOnly', 'View only.')}</strong> {t('members.viewOnlyDescription', 'You can see this member because they are in a program you have access to, but editing is restricted to the assigned staff member. Contact a program manager or supervisor if you need to be assigned to this member.')}
            </p>
          </div>
        )}
        {/* Program Selection (if not preselected) */}
        {!programId && !isEditMode && (
          <Card className="mb-6 border-blue-600 border-2 bg-blue-50 dark:bg-blue-900/10">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-blue-900 dark:text-blue-100 mb-4">
                {t('members.step1SelectProgram', 'Step 1: Select Program (Important!)')}
              </h2>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                <strong>{t('members.choose_which_program_this_member', 'Choose which program this member will be enrolled in.')}</strong> {t('members.this_controls_which_custom_fields', 'This controls which custom fields appear below.')}
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                {t('members.selectSpecificProgramHint', '• Select a specific program to show only relevant fields for that program')}<br />
                {t('members.showAllFieldsHint', '• Leave as "Show All Fields" to see every available custom field')}
              </p>

              <div className="mb-4">
                <Label htmlFor="program-select" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  {t('members.program', 'Program')}
                </Label>
                <Select
                  value={selectedProgramId ? String(selectedProgramId) : ''}
                  onValueChange={(value) => setSelectedProgramId(value ? parseInt(value) : undefined)}
                >
                  <SelectTrigger id="program-select">
                    <SelectValue placeholder={t('members.selectProgramOrShowAll', '-- Select Program or Show All Fields --')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">{t('members.selectProgramOrShowAll', '-- Select Program or Show All Fields --')}</SelectItem>
                    {programs?.filter(p => p.is_active).map((program) => (
                      <SelectItem key={program.id} value={String(program.id)}>
                        {program.name} ({program.program_type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!selectedProgramId && (
                <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-900/20">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <AlertTitle className="text-amber-900 dark:text-amber-100 font-semibold">
                    {t('members.no_program_selected', 'No program selected')}
                  </AlertTitle>
                  <AlertDescription className="text-amber-800 dark:text-amber-200 text-xs">
                    {t('members.all_custom_field_categories_will', 'All custom field categories will be shown below. This may include fields not relevant for your member.')}
                  </AlertDescription>
                </Alert>
              )}

              {selectedProgramId && programCategories.length > 0 && (
                <Alert className="border-green-500 bg-green-50 dark:bg-green-900/20">
                  <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <AlertTitle className="text-green-900 dark:text-green-100 font-semibold">
                    {t('members.program_selected', 'Program selected')}
                  </AlertTitle>
                  <AlertDescription className="text-green-800 dark:text-green-200 text-xs">
                    {t('members.showing_only_these_field_categories', 'Showing only these field categories:')} <strong>{programCategories.map(pc => pc.category).join(', ')}</strong>
                  </AlertDescription>
                </Alert>
              )}

              {selectedProgramId && programCategories.length === 0 && (
                <Alert className="border-green-500 bg-green-50 dark:bg-green-900/20">
                  <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <AlertTitle className="text-green-900 dark:text-green-100 font-semibold">
                    {t('members.program_selected_1', 'Program selected')}
                  </AlertTitle>
                  <AlertDescription className="text-green-800 dark:text-green-200 text-xs">
                    {t('members.allCategoriesShownConfigureToRestrict', 'All custom field categories will be shown. To restrict fields for this program, configure program-specific categories in Admin settings.')}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Demographics: data-driven (Profile Builder) for configured orgs,
            legacy fixed layout otherwise (TRUS-290). */}
        {useConfigured ? renderConfiguredLayout() : (
        <>
        {/* Core Fields Section */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {!programId && !isEditMode ? t('members.step2Prefix', 'Step 2: ') : ''}{t('members.basicInformation', 'Basic Information')}
            </h2>
            <Separator className="mb-6" />

            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12 sm:col-span-6">
                <Label htmlFor="first-name" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.first_name', 'First Name')} <RequiredMark field="first_name" />
                </Label>
                <Input
                  id="first-name"
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={(e) => handleCoreFieldChange('first_name', e.target.value)}
                  disabled={formLocked}
                  className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
                />
              </div>

              <div className="col-span-12 sm:col-span-6">
                <Label htmlFor="last-name" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.last_name', 'Last Name')} <RequiredMark field="last_name" />
                </Label>
                <Input
                  id="last-name"
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={(e) => handleCoreFieldChange('last_name', e.target.value)}
                  disabled={formLocked}
                  className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
                />
              </div>

              <div className="col-span-12 sm:col-span-6">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.email', 'Email')} <RequiredMark field="email" />
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleCoreFieldChange('email', e.target.value)}
                  placeholder={t('members.emailPlaceholder', 'member@example.com')}
                  disabled={formLocked}
                  className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
                />
              </div>

              <div className="col-span-12 sm:col-span-6">
                <Label htmlFor="phone" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.phone_number', 'Phone Number')} <RequiredMark field="phone_no" />
                </Label>
                <PhoneInput
                  id="phone"
                  required
                  value={formData.phone_no}
                  onChange={(v) => handleCoreFieldChange('phone_no', v)}
                  placeholder="(415) 336-2326"
                  disabled={formLocked}
                  className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('members.phoneFormatHint', 'Format: (xxx) xxx-xxxx - will be auto-formatted')}
                </p>
              </div>

              <div className="col-span-12 sm:col-span-6">
                <Label htmlFor="dob" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.date_of_birth', 'Date of Birth')} <RequiredMark field="dob" />
                </Label>
                <Input
                  id="dob"
                  type="date"
                  required
                  value={formData.dob}
                  onChange={(e) => handleCoreFieldChange('dob', e.target.value)}
                  disabled={formLocked}
                  className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
                />
              </div>

              <div className="col-span-12 sm:col-span-6">
                <Label htmlFor="gender" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  {t('members.gender', 'Gender')} <RequiredMark field="gender" />
                </Label>
                <Select
                  value={formData.gender}
                  onValueChange={(value) => handleCoreFieldChange('gender', value)}
                >
                  <SelectTrigger id="gender">
                    <SelectValue placeholder={t('members.select_gender', 'Select Gender...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {fieldOptions?.gender.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!isCoreFieldHidden('race') && (
              <div className="col-span-12 sm:col-span-6">
                <Label htmlFor="race" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  {t('members.race', 'Race')} <RequiredMark field="race" />
                </Label>
                <Select
                  value={formData.race}
                  onValueChange={(value) => handleCoreFieldChange('race', value)}
                >
                  <SelectTrigger id="race">
                    <SelectValue placeholder={t('members.select_race', 'Select Race...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {fieldOptions?.race.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}

              {!isCoreFieldHidden('ethnicity') && (
              <div className="col-span-12 sm:col-span-6">
                <Label htmlFor="ethnicity" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  {t('members.ethnicity', 'Ethnicity')} <RequiredMark field="ethnicity" />
                </Label>
                <Select
                  value={formData.ethnicity}
                  onValueChange={(value) => handleCoreFieldChange('ethnicity', value)}
                >
                  <SelectTrigger id="ethnicity">
                    <SelectValue placeholder={t('members.select_ethnicity', 'Select Ethnicity...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {fieldOptions?.ethnicity.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}

              {!isCoreFieldHidden('preferred_language') && (
              <div className="col-span-12 sm:col-span-6">
                <Label htmlFor="language" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  {t('members.primary_language', 'Primary Language')} <RequiredMark field="preferred_language" />
                </Label>
                <Select
                  value={formData.preferred_language}
                  onValueChange={(value) => handleCoreFieldChange('preferred_language', value)}
                >
                  <SelectTrigger id="language">
                    <SelectValue placeholder={t('members.select_language', 'Select Language...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {optionsWithCurrent(fieldOptions?.language, formData.preferred_language).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}

              {!isCoreFieldHidden('patient_no') && (
              <div className="col-span-12 sm:col-span-6">
                <Label htmlFor="patient-no" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.patientNumberOptional', 'Patient Number (Optional)')}
                </Label>
                <Input
                  id="patient-no"
                  type="text"
                  value={formData.patient_no}
                  onChange={(e) => handleCoreFieldChange('patient_no', e.target.value)}
                  placeholder={t('members.externalPatientId', 'External patient ID (optional)')}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('members.patientNumberHint', 'External identifier from another system (e.g., legacy EHR). Internal Member ID is auto-generated.')}
                </p>
              </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Insurance Information — hidden when all insurance fields are hidden by admin */}
        {!(isCoreFieldHidden('medicaid_id') && isCoreFieldHidden('medicare_id') && isCoreFieldHidden('commercial_insurance_id')) && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t('members.insurance_information', 'Insurance Information')}
            </h2>
            <Separator className="mb-6" />

            <div className="grid grid-cols-12 gap-6">
              {!isCoreFieldHidden('medicaid_id') && (
              <div className="col-span-12 sm:col-span-6 md:col-span-4">
                <Label htmlFor="medicaid-id" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.medicaid_id', 'Medicaid ID')} <RequiredMark field="medicaid_id" />
                </Label>
                <Input
                  id="medicaid-id"
                  type="text"
                  value={formData.medicaid_id}
                  onChange={(e) => handleCoreFieldChange('medicaid_id', e.target.value)}
                  placeholder={t('members.medicaid_id_1', 'Medicaid ID')}
                  disabled={formLocked}
                  className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
                />
              </div>
              )}

              {!isCoreFieldHidden('medicare_id') && (
              <div className="col-span-12 sm:col-span-6 md:col-span-4">
                <Label htmlFor="medicare-id" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.medicare_id', 'Medicare ID')} <RequiredMark field="medicare_id" />
                </Label>
                <Input
                  id="medicare-id"
                  type="text"
                  value={formData.medicare_id}
                  onChange={(e) => handleCoreFieldChange('medicare_id', e.target.value)}
                  placeholder={t('members.medicare_id_1', 'Medicare ID')}
                  disabled={formLocked}
                  className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
                />
              </div>
              )}

              {!isCoreFieldHidden('commercial_insurance_id') && (
              <div className="col-span-12 sm:col-span-6 md:col-span-4">
                <Label htmlFor="commercial-insurance-id" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.commercial_insurance_id', 'Commercial Insurance ID')} <RequiredMark field="commercial_insurance_id" />
                </Label>
                <Input
                  id="commercial-insurance-id"
                  type="text"
                  value={formData.commercial_insurance_id}
                  onChange={(e) => handleCoreFieldChange('commercial_insurance_id', e.target.value)}
                  placeholder={t('members.commercial_insurance_id_1', 'Commercial Insurance ID')}
                  disabled={formLocked}
                  className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
                />
              </div>
              )}
            </div>
          </CardContent>
        </Card>
        )}

        {/* Address Information */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t('members.address', 'Address')}
            </h2>
            <Separator className="mb-6" />

            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12">
                <Label htmlFor="street-address" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.street_address', 'Street Address')} <RequiredMark field="street_address" />
                </Label>
                <Input
                  id="street-address"
                  type="text"
                  value={formData.street_address}
                  onChange={(e) => handleCoreFieldChange('street_address', e.target.value)}
                  placeholder={t('members.street_address_1', 'Street Address')}
                  disabled={formLocked}
                  className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
                />
              </div>

              <div className="col-span-12 sm:col-span-6 md:col-span-4">
                <Label htmlFor="city" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.city', 'City')} <RequiredMark field="city" />
                </Label>
                <Input
                  id="city"
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleCoreFieldChange('city', e.target.value)}
                  placeholder={t('members.city_1', 'City')}
                  disabled={formLocked}
                  className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
                />
              </div>

              <div className="col-span-12 sm:col-span-6 md:col-span-4">
                <Label htmlFor="state" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  {t('members.state', 'State')} <RequiredMark field="state" />
                </Label>
                <SearchableSelect
                  id="state"
                  options={optionsWithCurrent(fieldOptions?.state, formData.state)}
                  value={formData.state}
                  onValueChange={(value) => handleCoreFieldChange('state', value)}
                  placeholder={t('members.select_state', 'Select State...')}
                  searchPlaceholder={t('members.search_state', 'Search states...')}
                  disabled={formLocked}
                  className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
                />
              </div>

              <div className="col-span-12 sm:col-span-6 md:col-span-2">
                <Label htmlFor="zip-code" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.zip_code', 'Zip Code')} <RequiredMark field="zip_code" />
                </Label>
                <Input
                  id="zip-code"
                  type="text"
                  value={formData.zip_code}
                  onChange={(e) => handleCoreFieldChange('zip_code', e.target.value)}
                  placeholder={t('members.zip_code_1', 'Zip Code')}
                  disabled={formLocked}
                  className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
                />
              </div>

              <div className="col-span-12 sm:col-span-6 md:col-span-2">
                <Label htmlFor="country" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  {t('members.country', 'Country')} <RequiredMark field="country" />
                </Label>
                <SearchableSelect
                  id="country"
                  options={optionsWithCurrent(fieldOptions?.country, formData.country)}
                  value={formData.country}
                  onValueChange={(value) => handleCoreFieldChange('country', value)}
                  placeholder={t('members.select_country', 'Select Country...')}
                  searchPlaceholder={t('members.search_country', 'Search countries...')}
                  disabled={formLocked}
                  className={`mt-1 ${formLocked ? 'opacity-60' : ''}`}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Custom Fields by Category */}
        {displayCategories?.map((category) => {
          // Skip empty/whitespace-only category names. A blank category string
          // (e.g. com_fields.category = '') would render an *untitled* section
          // here, while the very same fields also render under "Additional
          // Information" below (the `!f.category` bucket) — producing a
          // duplicate, unlabeled section (TRUS-236). Blank-category fields
          // belong only under Additional Information.
          if (!isNamedCategory(category)) return null;

          const categoryFields = (fields?.filter(
            (f) => f.category === category && !f.hidden
          ) || []).slice().sort(compareBySortOrder);

          if (categoryFields.length === 0) return null;

          return (
            <Card key={category} className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  {category}
                </h2>
                <Separator className="mb-6" />
                <div className="grid grid-cols-12 gap-6">
                  {categoryFields.map((field) => renderCustomField(field))}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {/* Uncategorized Custom Fields */}
        {fields?.filter((f) => !f.category && !f.hidden).length > 0 && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {t('members.additional_information', 'Additional Information')}
              </h2>
              <Separator className="mb-6" />
              <div className="grid grid-cols-12 gap-6">
                {(fields?.filter((f) => !f.category && !f.hidden) || [])
                  .slice()
                  .sort(compareBySortOrder)
                  .map((field) => renderCustomField(field))}
              </div>
            </CardContent>
          </Card>
        )}
        </>
        )}

        {/* Form Actions */}
        <div className="flex gap-4 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => onNavigate?.('members')}
            disabled={loading}
            size="lg"
          >
            {t('members.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading || sensitiveFieldsLocked || isViewOnly}
            loading={loading}
            icon={!loading ? <Save className="w-5 h-5" /> : undefined}
            iconPosition="left"
            size="lg"
            title={
              isViewOnly
                ? t('members.viewOnlyAccessTooltip', 'You have view-only access to this member. Only the assigned staff can edit.')
                : sensitiveFieldsLocked
                ? t('members.revealShieldedBeforeSaving', 'Reveal shielded data before saving')
                : undefined
            }
          >
            {loading
              ? t('members.saving', 'Saving...')
              : isViewOnly
              ? t('members.viewOnlyButton', 'View Only')
              : sensitiveFieldsLocked
              ? t('members.revealDataToSave', 'Reveal Data to Save')
              : isEditMode
              ? t('members.updateMember', 'Update Member')
              : t('members.createMember', 'Create Member')}
          </Button>
        </div>
      </form>

      {/* Data Shield Components */}
      <RevealStatusBar />
      <ReauthModal
        open={showReauthModal}
        onOpenChange={setShowReauthModal}
        onSuccess={activateRevealSession}
        effectivePolicy={effectivePolicy}
      />
    </div>
  );
}
